// Sheet/modal de configuración de video para plantillas. Se abre desde
// el botón "Configurar video" del ConfigurePanel y desde el AsistenteMode
// cuando el toggle Imagen/Video está en Video. Permite ajustar la
// duración (slider 4-12s por default, replicando el control de animar
// imagen) y togglear escenas múltiples (que duplica el costo).
//
// El modo "Video con influencer" reusa este sheet pero con un rango de
// 8-18s, SIN escenas múltiples (`showMultiScene={false}`) y con un
// `baseCost` de 1 token (el frame base que cobra el Generar), así el
// "Costo por video" del menú coincide con lo que se cobra (duración + 1).
// Ver ADR-099.
//
// Mobile: bottom sheet con drag handle iOS-style.
// Desktop: side panel a la derecha, mismo patrón que ModelPicker etc.

import React from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import { useExitTransition } from '../../../hooks/useExitTransition';

export interface VideoConfigSheetProps {
  open: boolean;
  duration: number;
  onDurationChange: (n: number) => void;
  multiScene: boolean;
  onMultiSceneChange: (v: boolean) => void;
  onClose: () => void;
  /** Tope del slider de duración. Default `VIDEO_DURATION_MAX` (12s, flujo de
   *  plantilla-video). El modo "Video con influencer" lo sube a 18s. */
  maxDuration?: number;
  /** Piso del slider de duración. Default `VIDEO_DURATION_MIN` (4s). El modo
   *  "Video con influencer" lo sube a 8s. */
  minDuration?: number;
  /** Si false, oculta el bloque "Escenas múltiples" (y el costo lo ignora).
   *  El modo "Video con influencer" no usa escenas múltiples. Default true. */
  showMultiScene?: boolean;
  /** Costo fijo (tokens) que se SUMA al costo por duración. El modo "Video con
   *  influencer" lo pone en 1 (el frame base que ya cobra el Generar) para que
   *  el "Costo por video" del menú coincida con lo cobrado. Default 0. */
  baseCost?: number;
  /** Créditos por segundo de video. Default 1 (flujo plantilla-video). El
   *  asistente lo pone en 2 (tarifa estándar 720p — ver utils/credits.ts)
   *  para que el "Costo por video" coincida con el badge del Generar. */
  costPerSecond?: number;
}

export const VIDEO_DURATION_MIN = 4;
export const VIDEO_DURATION_MAX = 12;
// Rango del modo "Video con influencer" (UGC): más largo que la plantilla-video.
export const VIDEO_INFLUENCER_DURATION_MIN = 8;
export const VIDEO_INFLUENCER_DURATION_MAX = 18;

export const VideoConfigSheet: React.FC<VideoConfigSheetProps> = ({
  open,
  duration,
  onDurationChange,
  multiScene,
  onMultiSceneChange,
  onClose,
  maxDuration = VIDEO_DURATION_MAX,
  minDuration = VIDEO_DURATION_MIN,
  showMultiScene = true,
  baseCost = 0,
  costPerSecond = 1,
}) => {
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false (al tocar Listo / cerrar) — así
  // se ve el deslizamiento inverso antes del unmount.
  const { mounted, animated } = useExitTransition(open, 300);

  // Clamp de la duración al rango activo. Importa cuando el usuario venía del
  // flujo plantilla-video (min 4) y entra al influencer (min 8): el valor
  // heredado podría quedar por debajo del piso. Mostramos el clamp y lo
  // sincronizamos al padre para que el payload no mande un valor fuera de rango.
  const clampedDuration = Math.min(maxDuration, Math.max(minDuration, duration));
  React.useEffect(() => {
    if (clampedDuration !== duration) onDurationChange(clampedDuration);
  }, [clampedDuration, duration, onDurationChange]);

  if (!mounted) return null;

  // Cuando el bloque de escenas múltiples está oculto, el costo lo ignora
  // (evita que un `multiScene` heredado del flujo plantilla-video duplique
  // el costo en el influencer).
  const effectiveMultiScene = showMultiScene && multiScene;
  const totalCost =
    clampedDuration * (effectiveMultiScene ? 2 : 1) * costPerSecond + baseCost;

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] max-h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[480px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {/* Mobile drag handle */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 md:py-4 shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="text-[15px] text-brand-primary active:opacity-50 transition-opacity md:hidden"
          >
            Cerrar
          </button>
          <h3 className="text-[16px] md:text-[17px] font-semibold text-brand-black/90 md:flex-1">
            Configurar video
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mx-5 h-px bg-black/[0.08] shrink-0" />

        {/* Contenido */}
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-6">
          {/* Duración */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label
                htmlFor="video-duration-slider"
                className="text-[14px] font-semibold text-brand-black/90"
              >
                Duración
              </label>
              <span className="text-[14px] font-semibold text-brand-primary tabular-nums">
                {clampedDuration}s
              </span>
            </div>
            <input
              id="video-duration-slider"
              type="range"
              min={minDuration}
              max={maxDuration}
              step={1}
              value={clampedDuration}
              onChange={(e) => onDurationChange(parseInt(e.target.value, 10))}
              className="w-full accent-brand-primary"
            />
            <div className="flex items-center justify-between text-[12px] text-[#3C3C43]/55 mt-1 tabular-nums">
              <span>{minDuration}s</span>
              <span>{maxDuration}s</span>
            </div>
          </div>

          {/* Escenas múltiples — oculto en el modo "Video con influencer"
              (showMultiScene=false). */}
          {showMultiScene && (
            <div className="flex items-start gap-3 rounded-2xl bg-[#F7F7F9] p-4">
              <div className="flex-1 min-w-0">
                <p
                  id="video-multiscene-label"
                  className="text-[14px] font-semibold text-brand-black/90"
                >
                  Escenas múltiples
                </p>
                <p className="text-[12.5px] text-[#3C3C43]/55 mt-0.5 leading-snug">
                  Genera varias escenas concatenadas en lugar de una sola.
                  Duplica el costo del video.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={multiScene}
                aria-labelledby="video-multiscene-label"
                onClick={() => onMultiSceneChange(!multiScene)}
                className={`shrink-0 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary/30 ${
                  multiScene ? 'bg-brand-primary' : 'bg-neutral-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    multiScene ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Costo total por video */}
          <div className="rounded-2xl border border-neutral-200 p-3.5 flex items-center justify-between">
            <span className="text-[13.5px] text-[#3C3C43]/70">
              Costo por video
            </span>
            <span className="inline-flex items-center gap-1.5 text-[14px] font-semibold text-brand-black/90">
              <img
                src="/images/token_icon.png"
                alt=""
                aria-hidden="true"
                className="w-4 h-4 object-contain"
              />
              {totalCost}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-5 pt-3 border-t border-black/[0.05]">
          <button
            type="button"
            onClick={onClose}
            className="w-full inline-flex items-center justify-center py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-sm transition-colors"
          >
            Listo
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
};
